import { useState } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  RotateCcw,
  AlertTriangle,
  Bot,
} from "lucide-react";
import { toast } from "sonner";

import { GET_AUDIT_FINDINGS } from "@/graphql/queries/audits";
import { RESOLVE_AUDIT_FINDING, COPY_AUDIT_REPORT } from "@/graphql/mutations/audits";
import { CopyForAi } from "@/components/admin/copy-for-ai";
import { MathContent } from "@/components/math/math-content";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 20;
const ALL = "__all";

type Finding = {
  id: string;
  auditor: string;
  model: string | null;
  severity: "critical" | "major" | "minor";
  category: string;
  comment: string;
  suggestedFix: string | null;
  status: string;
  resolutionNote: string | null;
  createdAt: string;
  resolvedAt: string | null;
  stale: boolean;
  missing: boolean;
};

type AuditQuestion = {
  filePath: string;
  pending: boolean;
  skillKey: string | null;
  skillName: string | null;
  question: string | null;
  answer: string | null;
  alternativeAnswers: string[];
  choices: string[] | null;
  hint: string | null;
  reviewedBy: string[];
  findings: Finding[];
};

type Auditor = {
  auditor: string;
  model: string | null;
  runs: number;
  lastRunAt: string | null;
  questionsReviewed: number;
  openCritical: number;
  openMajor: number;
  openMinor: number;
  openTotal: number;
};

const SEVERITY_LABEL: Record<Finding["severity"], string> = {
  critical: "Kritinė",
  major: "Svarbi",
  minor: "Smulki",
};

const SEVERITY_CLASS: Record<Finding["severity"], string> = {
  critical: "bg-red-100 text-red-800 border-red-200 dark:bg-red-950 dark:text-red-200 dark:border-red-900",
  major: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-900",
  minor: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700",
};

const STATUS_LABEL: Record<string, string> = {
  open: "Atvira",
  fixed: "Ištaisyta",
  dismissed: "Atmesta",
  superseded: "Pakeista naujesne peržiūra",
};

/**
 * What the Claude question auditors found, one card per question.
 *
 * **Grouped by question, not listed by finding**, because three auditors saying
 * three things about one question are one thing to fix — and the card says who
 * has read the question at all („Peržiūrėjo"), which is the half a list of
 * findings cannot show: an auditor that read it and found nothing has spoken too.
 *
 * Development only, like the rest of the content workbench: audits run locally
 * against the content being edited, from a Claude Code session
 * (`.claude/skills/audit-questions`), and their tables are not synced.
 */
export default function AuditsPage() {
  const [status, setStatus] = useState("open");
  const [auditor, setAuditor] = useState<string>(ALL);
  const [severity, setSeverity] = useState<string>(ALL);
  const [category, setCategory] = useState<string>(ALL);
  const [subject, setSubject] = useState<string>(ALL);
  const [queue, setQueue] = useState<string>(ALL);
  const [skill, setSkill] = useState("");
  const [page, setPage] = useState(0);

  const filters = {
    status,
    auditor: auditor === ALL ? undefined : auditor,
    severity: severity === ALL ? undefined : severity,
    category: category === ALL ? undefined : category,
    subject: subject === ALL ? undefined : subject,
    skill: skill.trim() || undefined,
    pending: queue === ALL ? undefined : queue === "pending",
  };

  const { data, loading, refetch } = useQuery<any>(GET_AUDIT_FINDINGS, {
    variables: { ...filters, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
    fetchPolicy: "cache-and-network",
  });

  const result = data?.auditFindings;
  const questions: AuditQuestion[] = result?.questions ?? [];
  const auditors: Auditor[] = result?.auditors ?? [];
  const totalQuestions: number = result?.totalQuestions ?? 0;
  const pages = Math.max(1, Math.ceil(totalQuestions / PAGE_SIZE));

  // Any filter change starts from the first page: page 4 of a narrower list is
  // usually past its end.
  const change = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setPage(0);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Užduočių auditas</h1>
          <p className="text-muted-foreground max-w-2xl">
            Ką rado užduočių auditoriai — Claude agentai, paleisti iš Claude Code sesijos, ir
            struktūrinis patikrinimas. Kiekvienoje kortelėje: kas užduotį peržiūrėjo, ką pastebėjo
            ir kas dar neištaisyta.
          </p>
        </div>
        <CopyForAi
          document={COPY_AUDIT_REPORT}
          variables={filters}
          pick={(d) => d?.copyAuditReport}
          label="Kopijuoti AI"
          hint="Atviros pastabos pagal filtrus, su kiekvienos užduoties JSON — įklijuok pokalbyje su AI, kuris jas ištaisys."
        />
      </div>

      {auditors.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {auditors.map((a) => (
            <button
              key={a.auditor}
              type="button"
              onClick={() => change(setAuditor)(auditor === a.auditor ? ALL : a.auditor)}
              className={`rounded-lg border p-3 text-left transition-colors hover:bg-muted/50 ${
                auditor === a.auditor ? "border-primary ring-1 ring-primary" : ""
              }`}
            >
              <div className="flex items-center gap-2 font-medium">
                <Bot className="h-4 w-4 text-muted-foreground" />
                {a.auditor}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {a.model === "none" ? "be AI" : a.model} · {a.runs} paleid. ·{" "}
                {a.questionsReviewed.toLocaleString("lt-LT")} užd. peržiūrėta
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                {a.openTotal === 0 ? (
                  <span className="text-green-700 dark:text-green-400">Atvirų pastabų nėra</span>
                ) : (
                  <>
                    {a.openCritical > 0 && (
                      <span className={`rounded border px-1.5 ${SEVERITY_CLASS.critical}`}>{a.openCritical} kritinės</span>
                    )}
                    {a.openMajor > 0 && (
                      <span className={`rounded border px-1.5 ${SEVERITY_CLASS.major}`}>{a.openMajor} svarbios</span>
                    )}
                    {a.openMinor > 0 && (
                      <span className={`rounded border px-1.5 ${SEVERITY_CLASS.minor}`}>{a.openMinor} smulkios</span>
                    )}
                  </>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect
          value={status}
          onChange={change(setStatus)}
          options={[
            ["open", "Atviros"],
            ["fixed", "Ištaisytos"],
            ["dismissed", "Atmestos"],
            ["superseded", "Pakeistos"],
            ["all", "Visos būsenos"],
          ]}
        />
        <FilterSelect
          value={auditor}
          onChange={change(setAuditor)}
          options={[[ALL, "Visi auditoriai"], ...auditors.map((a) => [a.auditor, a.auditor] as [string, string])]}
        />
        <FilterSelect
          value={severity}
          onChange={change(setSeverity)}
          options={[
            [ALL, "Visi svarbumai"],
            ["critical", "Kritinės"],
            ["major", "Svarbios"],
            ["minor", "Smulkios"],
          ]}
        />
        <FilterSelect
          value={category}
          onChange={change(setCategory)}
          options={[[ALL, "Visos kategorijos"], ...((result?.categories ?? []) as string[]).map((c) => [c, c] as [string, string])]}
        />
        <FilterSelect
          value={subject}
          onChange={change(setSubject)}
          options={[
            [ALL, "Visi dalykai"],
            ["math", "Matematika"],
            ["lithuanian", "Lietuvių k."],
          ]}
        />
        <FilterSelect
          value={queue}
          onChange={change(setQueue)}
          options={[
            [ALL, "Patvirtintos ir laukiančios"],
            ["approved", "Tik patvirtintos"],
            ["pending", "Tik laukiančios"],
          ]}
        />
        <Input
          value={skill}
          onChange={(e) => change(setSkill)(e.target.value)}
          placeholder="Įgūdžio raktas…"
          className="h-9 w-56"
        />
        <span className="ml-auto text-sm text-muted-foreground">
          {loading && !result ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              {totalQuestions.toLocaleString("lt-LT")} užd. · {(result?.totalFindings ?? 0).toLocaleString("lt-LT")} pastabų
            </>
          )}
        </span>
      </div>

      {auditors.length === 0 && !loading && (
        <Card>
          <CardHeader>
            <CardTitle>Auditas dar nepaleistas</CardTitle>
            <CardDescription>
              Struktūrinis patikrinimas (be AI):{" "}
              <code className="text-xs">bin/rails runner scripts/audit.rb -- structural</code>. Claude
              auditorius paleisk iš Claude Code sesijos: „audituok lt-g7-word-meaning užduotis" —
              naudojamas įgūdis <code className="text-xs">audit-questions</code>.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {auditors.length > 0 && questions.length === 0 && !loading && (
        <p className="py-10 text-center text-muted-foreground">Pagal šiuos filtrus pastabų nėra.</p>
      )}

      <div className="space-y-4">
        {questions.map((q) => (
          <QuestionCard key={`${q.pending}:${q.filePath}`} q={q} onChanged={() => refetch()} />
        ))}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm">
            {page + 1} / {pages}
          </span>
          <Button variant="outline" size="sm" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-auto min-w-36">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map(([v, label]) => (
          <SelectItem key={v} value={v}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function QuestionCard({ q, onChanged }: { q: AuditQuestion; onChanged: () => void }) {
  const root = q.pending ? "content/exercises-pending" : "content/exercises";
  return (
    <Card>
      <CardHeader className="space-y-1 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base">{q.skillName ?? q.skillKey}</CardTitle>
          {q.pending && <Badge variant="outline">laukia patvirtinimo</Badge>}
          <div className="ml-auto">
            <CopyForAi
              document={COPY_AUDIT_REPORT}
              variables={{ filePath: q.filePath, pending: q.pending, status: "all" }}
              pick={(d) => d?.copyAuditReport}
              hint="Šios užduoties pastabos ir JSON"
              variant="ghost"
              size="xs"
            />
          </div>
        </div>
        <CardDescription className="break-all font-mono text-xs">
          {root}/{q.filePath}
        </CardDescription>
        <div className="text-xs text-muted-foreground">
          Peržiūrėjo (dabartinę versiją):{" "}
          {q.reviewedBy.length ? q.reviewedBy.join(", ") : "niekas — užduotis pakeista po paskutinės peržiūros"}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {q.filePath.endsWith("/theory.html") ? (
          // A theory auditor's finding (TheoryAudit): the file is the skill's
          // theory, which has no question to show — the theory page does.
          <p className="text-sm">
            Teorijos failas.{" "}
            <a className="text-primary underline" href={`/admin/theory?skill=${encodeURIComponent(q.skillKey ?? "")}`}>
              Atidaryti teoriją
            </a>
          </p>
        ) : q.question == null ? (
          <p className="text-sm text-muted-foreground">Užduoties failo nebėra arba jis neperskaitomas.</p>
        ) : (
          <div className="rounded-md bg-muted/40 p-3 text-sm">
            <MathContent
              html={q.question}
              className="exercise-content [&_img]:max-h-40 [&_img]:max-w-[16rem] [&_p]:mb-1"
            />
            {q.choices && q.choices.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {q.choices.map((c, i) => (
                  <li
                    key={i}
                    className={`rounded border px-2 py-0.5 ${
                      c === q.answer ? "border-green-500 bg-green-50 dark:bg-green-950" : "bg-background"
                    }`}
                  >
                    <MathContent html={c} className="[&_p]:m-0" />
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-2 text-xs">
              <span className="text-muted-foreground">Atsakymas: </span>
              <span className="font-medium">{q.answer}</span>
              {q.alternativeAnswers.length > 0 && (
                <span className="text-muted-foreground"> · taip pat: {q.alternativeAnswers.join(" · ")}</span>
              )}
            </div>
          </div>
        )}

        <div className="space-y-3">
          {q.findings.map((f) => (
            <FindingRow key={f.id} f={f} onChanged={onChanged} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function FindingRow({ f, onChanged }: { f: Finding; onChanged: () => void }) {
  const [resolve, { loading }] = useMutation<any>(RESOLVE_AUDIT_FINDING);
  const [noting, setNoting] = useState<null | "fixed" | "dismissed">(null);
  const [note, setNote] = useState("");

  const submit = async (status: string, withNote?: string) => {
    const res = await resolve({ variables: { id: f.id, status, note: withNote || null } });
    const errors = res.data?.resolveAuditFinding?.errors ?? [];
    if (errors.length) {
      toast.error(errors[0]);
      return;
    }
    setNoting(null);
    setNote("");
    onChanged();
  };

  return (
    <div className="rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`rounded border px-1.5 py-0.5 font-medium ${SEVERITY_CLASS[f.severity]}`}>
          {SEVERITY_LABEL[f.severity]}
        </span>
        <code className="rounded bg-muted px-1.5 py-0.5">{f.category}</code>
        <span className="flex items-center gap-1 text-muted-foreground">
          <Bot className="h-3.5 w-3.5" />
          {f.auditor}
          {f.model && f.model !== "none" ? ` · ${f.model}` : ""}
        </span>
        <span className="text-muted-foreground">· {new Date(f.createdAt).toLocaleDateString("lt-LT")}</span>
        {f.status !== "open" && <Badge variant="secondary">{STATUS_LABEL[f.status] ?? f.status}</Badge>}
        {f.stale && f.status === "open" && (
          <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3.5 w-3.5" />
            {f.missing ? "užduoties nebėra" : "užduotis pakeista po šios pastabos"}
          </span>
        )}
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm">{f.comment}</p>
      {f.suggestedFix && (
        <div className="mt-2 rounded bg-muted/50 p-2 text-sm">
          <span className="font-medium">Siūlomas taisymas: </span>
          <span className="whitespace-pre-wrap">{f.suggestedFix}</span>
        </div>
      )}
      {f.resolutionNote && <p className="mt-2 text-xs text-muted-foreground">Pastaba: {f.resolutionNote}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {f.status === "open" ? (
          noting ? (
            <>
              <Input
                autoFocus
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={noting === "fixed" ? "Ką pakeitei? (nebūtina)" : "Kodėl atmeti? (nebūtina)"}
                className="h-8 max-w-sm"
                onKeyDown={(e) => e.key === "Enter" && submit(noting, note)}
              />
              <Button size="sm" disabled={loading} onClick={() => submit(noting, note)}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Išsaugoti"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setNoting(null)}>
                Atšaukti
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={() => setNoting("fixed")}>
                <CheckCircle2 className="mr-1 h-4 w-4" />
                Ištaisyta
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setNoting("dismissed")}>
                <XCircle className="mr-1 h-4 w-4" />
                Atmesti
              </Button>
            </>
          )
        ) : (
          <Button size="sm" variant="ghost" disabled={loading} onClick={() => submit("open")}>
            <RotateCcw className="mr-1 h-4 w-4" />
            Grąžinti į atviras
          </Button>
        )}
      </div>
    </div>
  );
}
